import { Dispatch, useCallback, useEffect, useMemo } from 'react';
import { connect, useDispatch, useSelector } from 'react-redux';
import { FieldArray, FieldArrayRenderProps } from 'react-final-form-arrays';
import { v4 as uuidv4 } from 'uuid';
import cn from 'classnames';

import { IOkvedSelectOption } from 'CorePackage/RegistryModule/interfaces/IOkvedSelectOption';
import { IKnmFormOkved } from 'KnmPackage/MainModule/interfaces/IKnmFormOkved';
import { IKnmFormRef } from 'KnmPackage/MainModule/interfaces/IKnmFormRef';
import {
  getKnmDataCreated,
  getKnmFormRef,
  getKnmIsErknmStatusFillInProcess,
  getKnmIsErknmStatusReadyForApprove,
  getKnmOkvedsSelectOptionsByParams,
} from 'KnmPackage/MainModule/selectors/knmSelectors';
import { Button } from 'UIPackage/MainModule/components/Button';
import { CloseButton } from 'UIPackage/MainModule/components/CloseButton';
import { SelectInputField } from 'UIPackage/MainModule/components/SelectInputField';
import { IRegistryExportOkvedFilterParams } from 'CorePackage/RegistryModule/interfaces/IRegistryExportOkvedFilterParams';
import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { Row } from 'UIPackage/FormModule/components/Row';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { RootActions } from 'CorePackage/MainModule/types/RootActions';
import { IInspectionResult } from 'APIPackage/CatalogsModule/interfaces/IInspectionResult';
import { ErknmInspectionResultMessage } from 'KnmErknmPackage/MainModule/components/ErknmInspectionResultMessageMessage';
import { InspectionResultActionTypeEnum } from 'APIPackage/CatalogsModule/enums/InspectionResultActionTypeEnum';
import { getFnsRegistryOkvedCodesStatus } from 'CorePackage/CatalogsModule/selectors/catalogsSelectors';
import { CatalogStatusesEnum } from 'CorePackage/CatalogsModule/enums/CatalogStatusesEnum';

import { knmSetSubjectData } from '../actions/knmActions';
import { IKnmCheckSubjectResult } from '../../../APIPackage/KnmModule/interfaces/IKnmCheckSubjectResult';
import { FnsCheckStatusesEnum } from '../../../KnmErknmPackage/ErknmSubjectModule/enums/FnsCheckStatusesEnum';
import { isLaterThanAdminSetting } from '../../../CorePackage/MainModule/validators/isLaterThanAdminSetting';
import { AdminSettingCodesEnum } from '../../../APIPackage/AdminModule/enums/AdminSettingCodesEnum';
import { IAdminSetting } from '../../../APIPackage/AdminModule/interfaces/IAdminSetting';
import { getAdminFetchSettings } from '../../../PersonalAccountPackage/AdminModule/selectors/adminSelectors';
import { catalogFnsRegistryOkvedCodesGet } from '../../../CorePackage/CatalogsModule/actions/catalogsActions';
import { FIELD_INCORRECTLY } from '../../../CorePackage/MainModule/constants/validationErrors';
import { getTitleFieldWithRedStar } from '../../../functions/getTitleFieldWithRedStar';
import styles from './KnmOkveds.module.scss';

interface IKnmOkvedsProps {
  blockId: string;
  checkSubjectResult?: IKnmCheckSubjectResult | null;
  getOkvedBlockId: (id: string) => string;
  inspectionResult?: IInspectionResult;
  name: string;
  okveds: IKnmFormOkved[] | undefined;
  organizationAutocomplete: boolean | undefined;
  subjectData?: Partial<IRegistryExportOkvedFilterParams>;
  watchOnly: boolean;
}

interface IKnmOkvedsStoreProps {
  created: string | null | undefined;
  formRef: IKnmFormRef | undefined;
  isKnmStatusFillInProcess: boolean;
  isKnmStatusReadyForApprove: boolean;
  settings: IAdminSetting[] | undefined;
  okvedCatalogStatus?: CatalogStatusesEnum;
}

const KnmOkvedsWithoutStore = ({
  blockId,
  checkSubjectResult,
  getOkvedBlockId,
  inspectionResult,
  name,
  okveds,
  organizationAutocomplete,
  subjectData,
  watchOnly,
  created,
  formRef,
  isKnmStatusFillInProcess,
  isKnmStatusReadyForApprove,
  settings,
  okvedCatalogStatus,
}: IKnmOkvedsProps & IKnmOkvedsStoreProps) => {
  const dispatch = useDispatch<Dispatch<RootActions>>();
  const push = formRef?.current.mutators.push;
  const remove = formRef?.current.mutators.remove;

  const okvedsSelectOptions = useSelector(getKnmOkvedsSelectOptionsByParams(subjectData));

  const isShowCheckResultMessage = isLaterThanAdminSetting(
    created,
    settings,
    AdminSettingCodesEnum.SHOW_CHECK_RESULT_MESSAGE_523_4_1_17,
  );

  // WORK-8361 Догружаем ОКВЭД, по условиям аналогичным валидатору.
  const isCheckOkved = useMemo(
    () =>
      isShowCheckResultMessage &&
      (isKnmStatusFillInProcess || isKnmStatusReadyForApprove) &&
      (checkSubjectResult?.egrip?.status === FnsCheckStatusesEnum.FOUND ||
        checkSubjectResult?.egrul?.status === FnsCheckStatusesEnum.FOUND),
    [isKnmStatusFillInProcess, isKnmStatusReadyForApprove, checkSubjectResult],
  );

  useEffect(() => {
    dispatch(knmSetSubjectData(subjectData));
  }, [dispatch, subjectData?.inn, subjectData?.ogrn]);

  const addOkved = useCallback(() => {
    if (!watchOnly && push) {
      const addingOkved: IKnmFormOkved = {
        okved: undefined,
        listId: uuidv4(),
      };

      push(name, addingOkved);
    }
  }, [name, push, watchOnly]);

  const makeRemoveOkvedHandler = (index: number) => () => !watchOnly && remove && remove(name, index);

  useEffect(() => {
    if (subjectData?.inn && isCheckOkved && (!okvedCatalogStatus || okvedCatalogStatus === CatalogStatusesEnum.IDLE)) {
      dispatch(catalogFnsRegistryOkvedCodesGet(subjectData.inn));
    }
  }, [subjectData, isCheckOkved, dispatch]);

  const isInspectionRed = inspectionResult?.resultActionType === InspectionResultActionTypeEnum.LIGHT_RED;
  const isInspectionYellow = inspectionResult?.resultActionType === InspectionResultActionTypeEnum.LIGHT_YELLOW;

  return (
    <FieldBlock
      label={getTitleFieldWithRedStar({
        label: 'ОКВЭД',
        field: name,
        isPm: true,
      })}
      id={blockId}
      button={
        <Button
          className="shared-btn-add"
          text="Добавить"
          variant="link"
          color="primary"
          type="button"
          disabled={watchOnly}
          onClick={addOkved}
        />
      }
    >
      {inspectionResult && <ErknmInspectionResultMessage inspectionResult={inspectionResult} />}
      <FieldArray name={name}>
        {({ fields, meta }: FieldArrayRenderProps<IKnmFormOkved, HTMLElement>) => {
          if (!fields.length) {
            return null;
          }

          return (
            <Row>
              {fields.map((okvedsFieldName, sIndex) => {
                const fieldValue: IKnmFormOkved = fields.value?.[sIndex];
                const id = getOkvedBlockId(fieldValue.listId);
                const isIncorrect = meta.error?.[sIndex]?.okved === FIELD_INCORRECTLY; // fieldValue.isInRegestry === false;
                const isMain = fieldValue.okved?.isMain;
                const isMainDisabled = organizationAutocomplete && isMain;
                const isEnabled = !watchOnly && (!isMainDisabled || isIncorrect);
                const customOkvedsSelectOptions: IOkvedSelectOption[] | undefined = okvedsSelectOptions?.map(
                  okvedSelectOption => ({
                    ...okvedSelectOption,
                    isDisabled: okveds?.some(
                      okvedsItem =>
                        okvedSelectOption.id === okvedsItem.okved?.id && okvedSelectOption.id !== fieldValue.okved?.id,
                    ),
                  }),
                );

                return (
                  <Row key={id} margin="medium" isListItem>
                    <SelectInputField
                      id={`${id}.okved`}
                      name={`${okvedsFieldName}.okved`}
                      placeholder={getTitleFieldWithRedStar({
                        label: 'Выберите ОКВЭД',
                        field: `${okvedsFieldName}.okved`,
                        isPm: true,
                      })}
                      options={customOkvedsSelectOptions}
                      isSearchable
                      disabled={!isEnabled}
                      inspectionResult={inspectionResult}
                      size="medium"
                      className={cn('shared-deleting-row', {
                        [styles.ResultYellow]: isInspectionYellow,
                        [styles.ResultRed]: isInspectionRed,
                      })}
                    />
                    {isEnabled && (
                      <CloseButton className="shared-deleting-row-btn" onClick={makeRemoveOkvedHandler(sIndex)} />
                    )}
                  </Row>
                );
              })}
            </Row>
          );
        }}
      </FieldArray>
    </FieldBlock>
  );
};

const mapStateToProps = (state: IRootState, ownProps: IKnmOkvedsProps): IKnmOkvedsStoreProps => ({
  created: getKnmDataCreated(state),
  formRef: getKnmFormRef(state),
  isKnmStatusFillInProcess: getKnmIsErknmStatusFillInProcess(state),
  isKnmStatusReadyForApprove: getKnmIsErknmStatusReadyForApprove(state),
  settings: getAdminFetchSettings(state),
  okvedCatalogStatus: ownProps?.subjectData?.inn
    ? getFnsRegistryOkvedCodesStatus(state, ownProps?.subjectData?.inn)
    : undefined,
});

export const KnmOkveds = connect(mapStateToProps)(KnmOkvedsWithoutStore);
